import * as z from "zod";
import { createFileRoute, useBlocker } from "@tanstack/react-router";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { HTTPError } from "ky";
import { api } from "@/lib/http";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UnsavedChangesDialog } from "@/components/ui/confirmation-dialog";
import { requiredString } from "@/lib/utils/zod";

export const Route = createFileRoute("/_app/user-settings/")({
  component: RouteComponent,
});

const updateUserDefaultBotName = z.object({
  default_bot_name: requiredString("Default meeting agent name required"),
});

type UpdateUserBotName = z.infer<typeof updateUserDefaultBotName>;

type UserBotName = {
  default_bot_name: string;
};

function defaultUserBotNameQueryOptions() {
  return queryOptions({
    queryKey: ["org-settings", "my-bot-name"],
    queryFn: () =>
      api.processing.raw
        .get("api/v1/org-settings/my-bot-name")
        .json<UserBotName>(),
  });
}
function RouteComponent() {
  const defaultUserBotNameQuery = useQuery(defaultUserBotNameQueryOptions());
  const queryClient = useQueryClient();

  const UserBotMeetingName = defaultUserBotNameQuery.data;

  const form = useForm<UpdateUserBotName>({
    resolver: zodResolver(updateUserDefaultBotName),
    defaultValues: async () => ({
      default_bot_name: (await defaultUserBotNameQuery.promise)
        .default_bot_name,
    }),
  });

  const updateUserMeetingBotName = useMutation({
    mutationFn: (data: UpdateUserBotName) => {
      return api.processing.raw
        .patch(`api/v1/org-settings/my-bot-name`, {
          json: data,
        })
        .json<UserBotName>();
    },
    onSuccess(data) {
      queryClient.setQueryData(["org-settings", "my-bot-name"], data);
      toast.success("Meeting agent name updated successfully");
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to update organization");
    },
  });

  const isDirty = form.formState.isDirty;

  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [isSavingAndLeaving, setIsSavingAndLeaving] = useState(false);
  const dismissedBlockerRef = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    withResolver: true,
    enableBeforeUnload: isDirty,
  });

  useEffect(() => {
    if (blocker.status !== "blocked") {
      dismissedBlockerRef.current = false;
      return;
    }
    if (!unsavedDialogOpen && !dismissedBlockerRef.current) {
      setUnsavedDialogOpen(true);
    }
  }, [blocker.status, unsavedDialogOpen]);

  const handleKeepEditing = useCallback(() => {
    dismissedBlockerRef.current = true;
    if (blocker.status === "blocked") {
      blocker.reset();
    }
    setUnsavedDialogOpen(false);
  }, [blocker]);

  const handleDiscardAndLeave = useCallback(() => {
    form.reset();
    setUnsavedDialogOpen(false);
    if (blocker.status === "blocked") {
      blocker.proceed();
    }
  }, [blocker, form]);

  const handleSaveAndLeave = useCallback(async () => {
    setIsSavingAndLeaving(true);
    try {
      await form.handleSubmit(
        async (values) => {
          await updateUserMeetingBotName.mutateAsync(values);
          form.reset(values);
          setUnsavedDialogOpen(false);
          if (blocker.status === "blocked") {
            blocker.proceed();
          }
        },
        () => {
          // Validation failed: stay on the page so the errors stay visible.
          handleKeepEditing();
        },
      )();
    } catch {
      setUnsavedDialogOpen(false);
      if (blocker.status === "blocked") {
        blocker.reset();
      }
    } finally {
      setIsSavingAndLeaving(false);
    }
  }, [blocker, form, handleKeepEditing, updateUserMeetingBotName]);

  if (defaultUserBotNameQuery.isLoading) {
    return (
      <CardContent className="flex items-center justify-center py-8">
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      </CardContent>
    );
  }

  if (defaultUserBotNameQuery.isError) {
    return (
      <div className="p-6 text-red-500">Failed to load meeting agent name.</div>
    );
  }

  function onSubmit(values: UpdateUserBotName) {
    updateUserMeetingBotName.mutate(values, {
      onSuccess() {
        form.reset(values);
      },
    });
  }
  return (
    <div className="space-y-4 p-6">
      {!UserBotMeetingName ? (
        <div>No meeting agent name found.</div>
      ) : (
        <div className="w-full p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Meeting Agent Name</CardTitle>
                  <CardDescription>
                    Update the agent name - this will be applied to all future
                    meetings.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="default_bot_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter meeting agent name"
                            disabled={updateUserMeetingBotName.isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <div className="flex justify-end gap-3">
                <Button
                  type="submit"
                  disabled={updateUserMeetingBotName.isPending || !isDirty}
                >
                  {updateUserMeetingBotName.isPending
                    ? "Saving..."
                    : "Save Changes"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}
      <UnsavedChangesDialog
        open={unsavedDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleKeepEditing();
        }}
        onKeepEditing={handleKeepEditing}
        onDiscardAndLeave={handleDiscardAndLeave}
        onSaveAndLeave={handleSaveAndLeave}
        isSaving={isSavingAndLeaving}
      />
    </div>
  );
}
